import { TimeInForceType } from '@/compiled_proto/com/celertech/orderrouting/api/enums/TimeInForceTypeProto';
import { OrderType } from '@/compiled_proto/com/celertech/positionmanager/api/enums/OrderTypeProto';
import { Side } from '@/compiled_proto/com/celertech/positionmanager/api/enums/SideProto';
import { submitStopOrder } from '@/services/OrderService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { selectSettings } from '@/state/reducers/settingSlice';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import { invalidStopPriceMessage } from '@/utils/hooks/useOrderBook';
import { UseOrderExecutionReturn } from '@/utils/hooks/useOrderExecution';
import useTotals from '@/utils/hooks/useTotals';
import { yupResolver } from '@hookform/resolvers/yup';
import BigNumber from 'bignumber.js';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { MdInfo } from 'react-icons/md';
import Tooltip from '../common/Tooltip';
import ExecutionInputController from '../inputs/ExecutionInputController';
import NumberInputDisplay from '../inputs/NumberInputDisplay';
import RHFNumberInput from '../inputs/RHFNumberInput';
import Select from '../inputs/Select';
import SlippageInput from '../inputs/SlippageInput';
import FormBody from './components/FormBody';
import FormCurrency from './components/FormCurrency';
import FormFooter from './components/FormFooter';
import FormOrderTypeSwitch from './components/FormOrderTypeSwitch';
import FormSubmit from './components/FormSubmit';
import Totals from './components/Totals';
import { StopOrderFormInput, StopOrderFormValues, stopOrderSchema } from './schema/stopOrderSchema';

const stopOrderInfo =
    'To limit risk of stops triggering on a temporary spread widening, Stops to Buy are triggered on the Bid, Stops to Sell are triggered on the Offer.';

const stopTypeOptions: { label: string; value: OrderType }[] = [
    { label: 'STOP MARKET', value: OrderType.STOP_MARKET },
    { label: 'STOP LIMIT', value: OrderType.STOP_LIMIT }
];

const durationOptions: { label: keyof typeof TimeInForceType; value: TimeInForceType }[] = [
    { label: 'DAY', value: TimeInForceType.DAY },
    { label: 'GTC', value: TimeInForceType.GTC }
];

const defaultValues: Partial<StopOrderFormInput> = {
    type: stopTypeOptions[0],
    stopPrice: null,
    quantity: null,
    duration: durationOptions[0],
    slippage: '0.00000'
};

interface StopOrderFormProps extends UseOrderExecutionReturn {}

function StopOrderForm(props: StopOrderFormProps) {
    const {
        activePair,
        ccy1,
        ccy2,
        sideState,
        currencyState,
        orderBook,
        instrument,
        activeCurrencyConfig,
        minOrderSize,
        orderDecimals,
        isCcy2Order
    } = props;

    const lang = useLanguage();
    const settings = useAppSelector(selectSettings);
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const [side, setSide] = sideState;
    const [currency, setCurrency] = currencyState;

    const { formatPrice, pip_size, defaultSlippage, price_decimals } = instrument;
    const { asks, bids, bestAsk, bestBid, bestPrice } = orderBook;
    const { increments } = activeCurrencyConfig;

    const form = useForm<StopOrderFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(
            stopOrderSchema({
                priceOptions: {
                    side,
                    bestAsk,
                    bestBid,
                    formattedBestAsk: formatPrice(bestAsk),
                    formattedBestBid: formatPrice(bestBid),
                    ccy2Order: isCcy2Order
                },
                quantityOptions: {
                    min_order_size: minOrderSize,
                    order_decimals: orderDecimals,
                    currencyOut: currency,
                    currencyPair: activePair?.show
                }
            })
        )
    });

    const totals = useTotals({
        form,
        activePair,
        side: side === Side.BUY ? Side.SELL : Side.BUY,
        ccy1,
        ccy2,
        ccy2Order: isCcy2Order,
        bids,
        asks
    });

    const {
        watch,
        reset,
        register,
        setValue,
        setError,
        clearErrors,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = form;

    const [stopType, stopPrice, slippage] = watch(['type.value', 'stopPrice', 'slippage']);

    const stopLimitPrice = useMemo(() => {
        const slip = BigNumber(+(slippage || 0)).dividedBy(Math.pow(10, pip_size || 0));
        const stop = +(stopPrice || 0);
        if (side === Side.BUY) return BigNumber(stop).plus(slip).toNumber();
        if (side === Side.SELL) return BigNumber(stop).minus(slip).toNumber();
        return stop;
    }, [stopPrice, slippage]);

    const onSubmit = async (data: StopOrderFormInput, e) => {
        const dataValidated = data as StopOrderFormValues;
        const slippage = dataValidated.slippage
            ? BigNumber(dataValidated.slippage)
                  .dividedBy(Math.pow(10, pip_size || 0))
                  .toString()
            : '0';
        if (dataValidated.quantity && credentials) {
            await submitStopOrder(
                {
                    spotPrice: bestPrice,
                    securityId: activePair?.celer,
                    currencyOut: currency,
                    quantity: dataValidated.quantity,
                    stopPrice: dataValidated.stopPrice,
                    timeInForce: dataValidated.duration.value as TimeInForceType,
                    orderType: dataValidated.type.value as OrderType,
                    limitPrice: stopLimitPrice,
                    side,
                    slippage
                },
                credentials,
                orderBook.settlementDate,
                currentAccount
            );
        }
    };

    const onError = (errors) => console.error(errors);

    useEffect(() => {
        setValue('stopPrice', getStopPrice(bestBid, bestAsk, side, isCcy2Order, price_decimals), {
            shouldValidate: true
        });
    }, [side]);

    useEffect(() => {
        const isCcy1Buy = side === Side.BUY && !isCcy2Order;
        const isCcy2Buy = side === Side.BUY && isCcy2Order;
        const isCcy1Sell = side === Side.SELL && !isCcy2Order;
        const isCcy2Sell = side === Side.SELL && isCcy2Order;

        if (stopPrice && bestBid && bestAsk) {
            if ((isCcy1Buy || isCcy2Sell) && +stopPrice <= bestBid) {
                const message = invalidStopPriceMessage(side, formatPrice(bestBid), isCcy2Order);
                setError('stopPrice', { type: 'stopPriceConstraint', message });
            } else if ((isCcy2Buy || isCcy1Sell) && +stopPrice >= bestAsk) {
                const message = invalidStopPriceMessage(side, formatPrice(bestAsk), isCcy2Order);
                setError('stopPrice', { type: 'stopPriceConstraint', message });
            } else {
                clearErrors('stopPrice');
            }
        }
    }, [bestBid, bestAsk, isCcy2Order, side]);

    const resetDefault = useCallback(() => {
        // resets all fields, set limit price to default worst price
        const resetValues: typeof defaultValues = {
            ...defaultValues,
            stopPrice: getStopPrice(bestBid, bestAsk, side, isCcy2Order, price_decimals),
            slippage: defaultSlippage
        };

        if ([TimeInForceType.DAY, TimeInForceType.GTC].includes(settings.trading.defaultStopOrderDuration.value)) {
            resetValues.duration = settings.trading.defaultStopOrderDuration;
        }

        reset(resetValues);
    }, [defaultValues, defaultSlippage, side, isCcy2Order, price_decimals, bestAsk, bestBid, settings]);

    useDidUpdate(() => resetDefault(), [currency]);

    useEffect(() => {
        resetDefault();
    }, [activePair?.celer]);

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit, onError)} className="relative flex h-full w-full text-brand-text">
                <FormBody side={side}>
                    <FormOrderTypeSwitch initialValue={side} onChange={setSide} />
                    <FormCurrency {...props} currency={currency} setCurrency={setCurrency} />
                    <div className="w-full relative">
                        <Tooltip content={stopOrderInfo} offset={6} className="max-w-sm">
                            <div className="absolute flex w-full justify-end right-0 top-0">
                                <MdInfo className="w-5 h-5" />
                            </div>
                        </Tooltip>
                        <ExecutionInputController name="type" label={`${lang.orderForm.stopType} *`} hasTooltipIcon>
                            <Select type="execution" options={stopTypeOptions} />
                        </ExecutionInputController>
                    </div>
                    <RHFNumberInput
                        label={`${lang.orderForm.stopPrice} *`}
                        placeholder={lang.orderForm.stopPrice}
                        {...register('stopPrice')}
                        step={BigNumber(1)
                            .dividedBy(Math.pow(10, pip_size || 0))
                            .toNumber()}
                        currency={activeCurrencyConfig.show}
                    />
                    <RHFNumberInput
                        {...register('quantity')}
                        label={`${lang.orderForm.quantity} *`}
                        placeholder={lang.orderForm.quantity}
                        min={minOrderSize}
                        step={increments}
                        currency={activeCurrencyConfig.show}
                    />
                    <ExecutionInputController name="duration" label={`${lang.orderForm.duration} *`}>
                        <Select type="execution" options={durationOptions} />
                    </ExecutionInputController>
                    {stopType === OrderType.STOP_LIMIT && (
                        <SlippageInput
                            {...register('slippage')}
                            label={lang.orderForm.slippage}
                            placeholder={lang.orderForm.slippage}
                            defaultSlippage={defaultSlippage}
                            step={0.1}
                        />
                    )}

                    {stopType === OrderType.STOP_LIMIT && (
                        <Tooltip
                            content="The Stop Limit price is placed as the Stop Price +/- Slippage"
                            className="max-w-fit">
                            <ExecutionInputController
                                name="stopLimitPrice"
                                label={`${lang.orderForm.stopLimitPrice} *`}
                                value={stopLimitPrice}>
                                <NumberInputDisplay placeholder={lang.orderForm.stopLimitPrice} disabled />
                            </ExecutionInputController>
                        </Tooltip>
                    )}
                    <Totals {...totals} />
                </FormBody>
                <FormFooter>
                    <FormSubmit
                        side={side}
                        isValid={isValid}
                        disabled={isSubmitting || !isValid || totals.disableSubmit}
                        noSettlementDate={!orderBook.settlementDate}
                    />
                </FormFooter>
            </form>
        </FormProvider>
    );
}

export default StopOrderForm;

export const convertToStopPrice = (price: number, side: Side, isCcy2Order: boolean, priceDecimals?: number) => {
    const isCcy1Buy = side === Side.BUY && !isCcy2Order;
    const isCcy2Buy = side === Side.BUY && isCcy2Order;
    const isCcy1Sell = side === Side.SELL && !isCcy2Order;
    const isCcy2Sell = side === Side.SELL && isCcy2Order;

    if (isCcy1Buy || isCcy2Sell)
        return BigNumber(price)
            .multipliedBy(1.001)
            .decimalPlaces(priceDecimals || 0)
            .toNumber();
    else if (isCcy2Buy || isCcy1Sell)
        return BigNumber(price)
            .multipliedBy(0.999)
            .decimalPlaces(priceDecimals || 0)
            .toNumber();
    else return price;
};

export const getStopPrice = (
    bestBid: number,
    bestAsk: number,
    side: Side,
    isCcy2Order: boolean,
    priceDecimals?: number
) => {
    const isCcy1Buy = side === Side.BUY && !isCcy2Order;
    const isCcy2Buy = side === Side.BUY && isCcy2Order;
    const isCcy1Sell = side === Side.SELL && !isCcy2Order;
    const isCcy2Sell = side === Side.SELL && isCcy2Order;

    if (bestBid && bestAsk) {
        if (isCcy1Buy || isCcy2Sell) return convertToStopPrice(bestBid, side, isCcy2Order, priceDecimals);
        else if (isCcy2Buy || isCcy1Sell) return convertToStopPrice(bestAsk, side, isCcy2Order, priceDecimals);
    }

    return 0;
};
